import { api } from './api';

// Postventa (post-sale service outreach) API — tenant/advisor-scoped.
// NOTE: bare '/postventa' paths — the api client already prefixes '/api'.

// List vehicles (soonest service-due first). Pass { advisor_id, limit } to
// filter, or { asesor } to scope by advisor (asesor-scoped targeting):
//   asesor='me'      -> my clients (rows whose Asesor == the logged-in user)
//   asesor='none'    -> unassigned (no asesor on file)
//   asesor='me+none' -> my clients + unassigned
//   asesor='all'     -> everyone (default)
//   asesor='<name>'  -> a specific advisor by name (admins hand-picking)
// Pass { ejecutivo } to scope by ejecutivo de citas (appointment executive):
//   ejecutivo='me'     -> my clients as ejecutivo (rows whose Ejecutivo == me)
//   ejecutivo='<name>' -> a specific ejecutivo by name (admins hand-picking)
export const listPostventaVehicles = (params = {}) =>
  api.get('/postventa/vehicles', { params });

// Asesor <-> user <-> child-agent mapping (admin-curated, tenant setting).
// GET returns { asesores: [...], my_asesor } so the page can default the
// "Contactar como" selector to the logged-in user's asesor.
export const getPostventaAsesores = () => api.get('/postventa/asesores');
export const updatePostventaAsesores = (asesores) =>
  api.put('/postventa/asesores', { asesores });

// Ejecutivo-de-citas catalog (appointment executives — DISTINCT from the B.PRO
// asesor de servicio). Admin-curated tenant setting, edited in Equipo.
// GET returns { ejecutivos: [{nombre,email?,telefono?,sucursal?}], my_ejecutivo }.
export const getPostventaEjecutivosCitas = () =>
  api.get('/postventa/ejecutivos-citas');
export const updatePostventaEjecutivosCitas = (ejecutivos) =>
  api.put('/postventa/ejecutivos-citas', { ejecutivos });

// Assign (or clear) the ejecutivo de citas on one or many cartera rows.
// body = { vins: [...], ejecutivo_citas }: a blank/omitted ejecutivo_citas
// CLEARS the assignment. Single-row menu sends one VIN; bulk sends the scope
// selection. Returns { assigned, errors, total, ejecutivo_citas }.
export const assignPostventaEjecutivo = ({ vins, ejecutivo_citas } = {}) =>
  api.post('/postventa/vehicles/assign-ejecutivo', { vins, ejecutivo_citas });

// Fetch one vehicle by VIN.
export const getPostventaVehicle = (vin) =>
  api.get(`/postventa/vehicles/${encodeURIComponent(vin)}`);

// Voice + WhatsApp interactions for one vehicle ({ calls, whatsapp }).
export const getPostventaInteractions = (vin) =>
  api.get(`/postventa/vehicles/${encodeURIComponent(vin)}/interactions`);

// oct20 CADENCE CAP (Sergio): "Reanudar contacto" on a vehicle the contact
// cadence stopped after N cycles without contact. Resets the per-due-service
// counter (logged who/when) and re-queues the stopped dial rows; the backend
// answers { followup, label, requeued, campaigns_resumed, relaunched_by }.
export const relaunchPostventaFollowup = (vin) =>
  api.post(`/postventa/vehicles/${encodeURIComponent(vin)}/followup/relaunch`);

// Create/update one vehicle (idempotent VIN-upsert).
export const upsertPostventaVehicle = (vin, body) =>
  api.put(`/postventa/vehicles/${encodeURIComponent(vin)}`, body);

// Bulk import (idempotent VIN-upsert; never clobbers contact history).
export const importPostventaVehicles = (vehicles) =>
  api.post('/postventa/import', { vehicles });

// Delete a vehicle by VIN.
export const deletePostventaVehicle = (vin) =>
  api.delete(`/postventa/vehicles/${encodeURIComponent(vin)}`);

// Simple tenant KPIs.
export const getPostventaMetrics = () =>
  api.get('/postventa/metrics');

// Export vehicles as .xlsx (backend returns a binary blob).
export const exportPostventaVehicles = () =>
  api.get('/postventa/export', { responseType: 'blob' });

// Managed Postventa locations (sucursales), admin-curated in Equipo.
export const getPostventaLocations = () => api.get('/postventa/locations');
export const updatePostventaLocations = (locations) =>
  api.put('/postventa/locations', { locations });

// B.PRO data freshness for the caller's tenant (admin/manager). Returns
// { tenant: { cartera:{last_import_at,last_import_age_days,stale,...},
//   service_refresh:{...}, calendar_backend:{...}, stale }, overall_stale }.
// Powers the "Cartera actualizada hace X días" badge (red when stale).
export const getPostventaFreshness = () => api.get('/postventa/freshness');

// Manually trigger the postventa WhatsApp reminder cadence (button-only path).
// Product decision (2026-08-29): postventa OUTBOUND is HUMAN-CLICKED — the
// autonomous daily loop is default-OFF, so this endpoint is how reminders go
// out. Always PREVIEW first with { dryRun: true } to get due/sent/skipped/
// failed counts, show the operator HOW MANY will be sent, then confirm and call
// again with { dryRun: false } for the real send. Admin/super_admin only.
// Returns { tenant_id, dry_run, enabled, due, sent, informativo_sent,
//   skipped_no_phone, skipped_bucket_off, skipped_opted_out, skipped_not_cold,
//   skipped_staff, skipped_idempotent_24h, failed, by_priority }.
export const sendDuePostventaReminders = ({ dryRun = false } = {}) =>
  api.post('/postventa/reminders/send-due', null, { params: { dry_run: dryRun } });

// Launch a voice outreach campaign for selected VINs.
// body REQUIRES { vins, agent_id, caller_ids: [...], name?, asesor_name? } — a
// vins-only POST 422s. asesor_name (the "Contactar como" asesor) is stamped on
// every contact so the cita/CRM/B.PRO write-back + child-agent routing carry it.
// Returns { success, campaign_id, contacts, skipped_no_phone, skipped_pilot,
//   pilot_mode }. skipped_pilot lists VINs NOT dialed because postventa pilot
//   mode is ON and the number is not on the whitelist (calls are gated exactly
//   like WhatsApp sends).
export const startPostventaOutreach = (body) =>
  api.post('/postventa/outreach', body);
